import {
  MenuItemProps,
  toCamelCase,
  toWSDateString,
  toWSMoneyString,
  useIsMobile,
  WSButton,
  WSCircleIconProps,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSScreenSizesType,
  WSTable,
  WSTableColumnJustifyType,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IInvoice,
  IInvoiceTemplate,
  IMemberClient,
  InvoiceStatus,
  MemberWorkFlowStatus,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { addBusinessDays, addDays } from "date-fns";
import React, { ReactNode, useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Tooltip } from "../../../components/Tooltip";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useSendInvoiceReminder } from "../../../query/payments/mutations";
import {
  useMemberClientsQuery,
  usePayoutSettings
} from "../../../query/payments/queries";
import { getIsInstantPayoutAllowed } from "../../../query/payments/selectors";
import { useMemberProfile } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { isSameDate } from "../../../utils/dates";
import { openInNewTab } from "../../../utils/openInNewTab";
import {
  calculateInvoiceTempalteDueDate,
  calculateLineItemsTotal,
  getNextInvoiceSendDate
} from "../../utils";
import {
  useGetInvoiceActions,
  useGetInvoiceTemplateActions
} from "../InvoiceActions/InvoiceActions";

const defaultSort = (a: InvoicesTableItem, b: InvoicesTableItem) => {
  const aDueDate =
    a.data.invoice?.dueDate ||
    (a.data.invoiceTemplate
      ? calculateInvoiceTempalteDueDate(a.data.invoiceTemplate)
      : new Date());

  const bDueDate =
    b.data.invoice?.dueDate ||
    (b.data.invoiceTemplate
      ? calculateInvoiceTempalteDueDate(b.data.invoiceTemplate)
      : new Date());

  return aDueDate > bDueDate ? 1 : -1;
};

type InvoicesTableItem = WSTableItem<InvoicesTableItemData>;

type InvoicesTableItemData = {
  memberClient?: IMemberClient;
  invoice?: IInvoice;
  invoiceTemplate?: IInvoiceTemplate;
};

type InvoicesTableProps = {
  title?: string;
  invoices?: IInvoice[];
  invoiceTemplates?: IInvoiceTemplate[];
  emptyState?: ReactNode;
  listName?: "paid" | "list";
  sort?: (a: InvoicesTableItem, b: InvoicesTableItem) => number;
  pageLimit?: number;
} & WSElementProps;

export const InvoicesTable: React.FC<InvoicesTableProps> = ({
  title,
  invoices = [],
  invoiceTemplates = [],
  emptyState,
  listName,
  sort,
  pageLimit,
  ...elementProps
}) => {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const userId = useUserId();
  const payoutSettingsQuery = usePayoutSettings(userId);
  const memberClientsQuery = useMemberClientsQuery();
  const [limit, setLimit] = useState(pageLimit || -1);
  const getInvoiceActions = useGetInvoiceActions();
  const getInvoiceTemplateActions = useGetInvoiceTemplateActions();
  const featureFlagsQuery = useFeatureFlags();
  const memberQuery = useMemberProfile(userId);

  const onView = useCallback(
    (item: InvoicesTableItem) => {
      if (item.data.invoice) {
        history.push({
          pathname: `/member/invoices${listName ? `/${listName}` : ""}/${
            item.id
          }`,
          search: location.search
        });
      } else if (item.data.invoiceTemplate) {
        history.push({
          pathname: `/member/invoices${
            listName ? `/${listName}` : ""
          }/template/${item.id}`,
          search: location.search
        });
      }
    },
    [listName, location.search]
  );

  const onViewInNewTab = useCallback(
    (item: InvoicesTableItem) => {
      const listNameString = listName ? `/${listName}` : "";

      if (item.data.invoice) {
        openInNewTab(
          `/member/invoices${listNameString}/${item.id}${location.search}`
        );
      } else if (item.data.invoiceTemplate) {
        openInNewTab(
          `/member/invoices${listNameString}/template/${item.id}${location.search}`
        );
      }
    },
    [listName, location.search]
  );

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          memberClientsQuery,
          payoutSettingsQuery,
          featureFlagsQuery,
          memberQuery
        }}
      >
        {({
          memberClientsQuery: { data: memberClients },
          payoutSettingsQuery: { data: payoutSettings },
          featureFlagsQuery: { data: featureFlags },
          memberQuery: { data: member }
        }) => {
          let data: InvoicesTableItem[] = [
            ...invoices.map(invoice => ({
              id: invoice.invoiceId,
              data: {
                invoice,
                memberClient: memberClients.find(
                  memberClient =>
                    memberClient.memberClientId === invoice.memberClientId
                )
              },
              icon: buildInvoiceIcon(invoice)
            })),
            ...invoiceTemplates.map(invoiceTemplate => ({
              id: invoiceTemplate.invoiceTemplateId,
              data: {
                invoiceTemplate,
                memberClient: memberClients.find(
                  memberClient =>
                    memberClient.memberClientId ===
                    invoiceTemplate.invoiceData.memberClientId
                )
              },
              icon: buildInvoiceTemplateIcon(invoiceTemplate)
            }))
          ];

          const sortFn = sort || defaultSort;
          data.sort(sortFn);

          const limitedData = limit > 0 ? data.slice(0, limit) : data;

          return data.length > 0 ? (
            <>
              {title && <WSText.Heading3 mb="M">{title}</WSText.Heading3>}

              <WSTable<InvoicesTableItemData>
                data-testid={toCamelCase(title || "", "invoices", "table")}
                showIcon
                tableData={limitedData}
                columns={[
                  {
                    config: {
                      gridTemplateSizeMax: "0.7fr"
                    },
                    renderFunction: ({ data: { memberClient } }) => (
                      <>
                        <WSText
                          mb={memberClient?.name ? "XS" : undefined}
                          color="grey700"
                        >
                          {memberClient?.company || memberClient?.emailTo}
                        </WSText>
                        <WSText.Heading5 color="grey600">
                          {memberClient?.name}
                        </WSText.Heading5>
                      </>
                    )
                  },
                  ...(featureFlags.customPayablesColumns
                    ? [
                        {
                          config: {
                            gridTemplateSizeMax: "0.4fr",
                            justify: "center" as WSTableColumnJustifyType,
                            hideOnScreens: ["XS", "S"] as WSScreenSizesType[]
                          },
                          renderFunction: ({
                            data: { invoice }
                          }: WSTableItem<any>) =>
                            invoice && (
                              <WSText mb="XS" color="grey700">
                                {invoice.invoiceNumber}
                              </WSText>
                            )
                        }
                      ]
                    : []),
                  ...(featureFlags.customPayablesColumns
                    ? [
                        {
                          config: {
                            gridTemplateSizeMax: "0.7fr",
                            justify: "end" as WSTableColumnJustifyType,
                            hideOnScreens: ["XS", "S"] as WSScreenSizesType[]
                          },
                          renderFunction: ({
                            data: { invoice, invoiceTemplate }
                          }: WSTableItem<InvoicesTableItemData>) => {
                            let description = "";
                            if (invoice && invoice.lineItems.length > 0) {
                              description =
                                invoice.lineItems[0].description ?? "";
                            } else if (
                              invoiceTemplate &&
                              invoiceTemplate.invoiceData?.lineItems.length > 0
                            ) {
                              description =
                                invoiceTemplate.invoiceData?.lineItems[0]
                                  .description ?? "";
                            }
                            return (
                              <WSText
                                mb="XS"
                                color="grey700"
                                responsive
                                title={description}
                              >
                                {description}
                              </WSText>
                            );
                          }
                        }
                      ]
                    : []),
                  {
                    config: {
                      gridTemplateSizeMax: "0.6fr",
                      justify: "end",
                      hideOnScreens: ["XS", "S"]
                    },
                    renderFunction: ({ data: { invoice } }) =>
                      invoice ? (
                        invoice.status === InvoiceStatus.Overdue ? (
                          <EmailClientButton invoiceId={invoice.invoiceId} />
                        ) : invoice.status ===
                          InvoiceStatus.PaymentInTransit ? (
                          <EmailClientButton invoiceId={invoice.invoiceId} />
                        ) : getIsInstantPayoutAllowed(
                            invoice,
                            payoutSettings,
                            member
                          ) ? (
                          <InstantPayoutRequestButton
                            invoiceId={invoice.invoiceId}
                          />
                        ) : null
                      ) : null
                  },
                  {
                    config: {
                      gridTemplateSizeMax: "0.6fr",
                      justify: "end"
                    },
                    renderFunction: ({
                      data: { invoice, invoiceTemplate }
                    }) => (
                      <WSFlexBox direction="column" alignItems="flex-end">
                        <WSText
                          mb={isMobile ? undefined : "XS"}
                          formatMoney
                          color="grey700"
                        >
                          {invoice
                            ? calculateLineItemsTotal(invoice.lineItems)
                            : invoiceTemplate
                            ? calculateLineItemsTotal(
                                invoiceTemplate.invoiceData.lineItems
                              )
                            : null}
                        </WSText>
                        {invoice
                          ? renderInvoiceLatestUpdate(invoice)
                          : invoiceTemplate
                          ? renderInvoiceTemplateLatestUpdate(invoiceTemplate)
                          : null}
                      </WSFlexBox>
                    )
                  }
                ]}
                onRowClick={onView}
                onRowControlClick={onViewInNewTab}
                onRowMousewheelClick={onViewInNewTab}
                rowActions={item => {
                  const {
                    data: { memberClient, invoice, invoiceTemplate }
                  } = item;
                  const menuItems: MenuItemProps[] = [];

                  menuItems.push({
                    icon: "search",
                    label: "View",
                    onClick: () => {
                      onView(item);
                    }
                  });

                  if (invoice && memberClient) {
                    menuItems.push(...getInvoiceActions(invoice, memberClient));
                  } else if (invoiceTemplate && memberClient) {
                    menuItems.push(
                      ...getInvoiceTemplateActions(
                        invoiceTemplate,
                        memberClient
                      )
                    );
                  }

                  return menuItems;
                }}
              />

              {pageLimit && data.length > limit && (
                <WSFlexBox.CenterX mt="M">
                  <WSButton.Link
                    onClick={() => {
                      setLimit(limit + pageLimit);
                    }}
                  >
                    Load more
                  </WSButton.Link>
                </WSFlexBox.CenterX>
              )}
            </>
          ) : emptyState ? (
            emptyState
          ) : null;
        }}
      </WSQueries>
    </WSElement>
  );
};

const renderInvoiceLatestUpdate = (invoice: IInvoice) =>
  invoice.status === InvoiceStatus.Draft ? (
    isSameDate(invoice.createdAt, invoice.updatedAt) ? (
      <WSText.Heading5 color="grey600" align="right">
        Created {toWSDateString(invoice.createdAt, "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Last updated {toWSDateString(invoice.updatedAt, "monthDate")}
      </WSText.Heading5>
    )
  ) : [
      InvoiceStatus.Pending,
      InvoiceStatus.Open,
      InvoiceStatus.Overdue
    ].includes(invoice.status) &&
    invoice.events.clientResolvedDisputeAt &&
    new Date() < addDays(invoice.events.clientResolvedDisputeAt, 2) ? (
    <WSText.Heading5 color="grey600" align="right">
      Dispute resolved at{" "}
      {toWSDateString(invoice.events.clientResolvedDisputeAt, "monthDate")}
    </WSText.Heading5>
  ) : [
      InvoiceStatus.Pending,
      InvoiceStatus.Open,
      InvoiceStatus.Overdue
    ].includes(invoice.status) &&
    invoice.client.workflowStatus === ClientWorkFlowStatus.Declined ? (
    <Tooltip
      trigger={
        <WSFlexBox.CenterY>
          <WSText.Heading5 color="grey600" align="right">
            Client rejected invoice
          </WSText.Heading5>
          <WSIcon name="info" size="XS" color="grey600" ml="XS" />
        </WSFlexBox.CenterY>
      }
      showInfoIcon={false}
    >
      <WSText.Heading5 color="white">Reject comment:</WSText.Heading5>
      <WSText.Heading5 color="white">{invoice.client.comment}</WSText.Heading5>
    </Tooltip>
  ) : invoice.status === InvoiceStatus.Pending ? (
    <WSText.Heading5 color="grey600" align="right">
      Invoice is pending
    </WSText.Heading5>
  ) : invoice.status === InvoiceStatus.Open ? (
    addDays(new Date(), 3) > invoice.dueDate ? (
      <WSText.Heading5 color="yellow500" align="right">
        Due soon ({toWSDateString(invoice.dueDate, "monthDate")})
      </WSText.Heading5>
    ) : invoice.events.emailViewedAt &&
      invoice.events.emailViewedAt.length > 0 ? (
      <WSText.Heading5 color="grey600" align="right">
        Last viewed{" "}
        {toWSDateString(
          invoice.events.emailViewedAt[invoice.events.emailViewedAt.length - 1],
          "monthDate"
        )}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Sent {toWSDateString(invoice.events.openedAt, "monthDate")}
      </WSText.Heading5>
    )
  ) : invoice.status === InvoiceStatus.Overdue ? (
    <WSText.Heading5 color="red500" align="right">
      Overdue ({toWSDateString(invoice.dueDate, "monthDate")})
    </WSText.Heading5>
  ) : invoice.status === InvoiceStatus.Paid ? (
    invoice.events.depositedAt ? (
      <WSText.Heading5 color="grey600" align="right">
        {toWSMoneyString(Math.abs(invoice.amountDetails?.memberGross || 0))}{" "}
        arrives by{" "}
        {toWSDateString(
          addBusinessDays(invoice.events.depositedAt, 1),
          "monthDate"
        )}
      </WSText.Heading5>
    ) : invoice.events.instantPayoutAt ? (
      <WSText.Heading5 color="grey600" align="right">
        Deposited instantly on{" "}
        {toWSDateString(invoice.events.instantPayoutAt, "monthDate")}
      </WSText.Heading5>
    ) : invoice.member.payoutPreferences === PayoutPreferences.Instant ? (
      <WSText.Heading5 color="grey600" align="right">
        Expected instant deposit {toWSDateString(new Date(), "monthDate")}
      </WSText.Heading5>
    ) : invoice.amountDetails ? (
      <WSText.Heading5 color="grey600" align="right">
        Expected direct deposit{" "}
        {toWSDateString(invoice.events.estimatedDepositAt, "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Paid outside of Wingspan on{" "}
        {toWSDateString(invoice.events.paidAt, "monthDate")}
      </WSText.Heading5>
    )
  ) : invoice.status === InvoiceStatus.PaymentInTransit ? (
    <WSText.Heading5 color="grey600" align="right">
      Waiting on client's wire transfer to arrive
    </WSText.Heading5>
  ) : null;

const renderInvoiceTemplateLatestUpdate = (invoiceTemplate: IInvoiceTemplate) =>
  invoiceTemplate.status === FrequencyAndScheduleStatus.Draft ? (
    isSameDate(invoiceTemplate.createdAt, invoiceTemplate.updatedAt) ? (
      <WSText.Heading5 color="grey600" align="right">
        Created {toWSDateString(invoiceTemplate.createdAt, "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Last updated {toWSDateString(invoiceTemplate.updatedAt, "monthDate")}
      </WSText.Heading5>
    )
  ) : invoiceTemplate.status === FrequencyAndScheduleStatus.Active ? (
    invoiceTemplate.frequency ? (
      <WSText.Heading5 color="grey600" align="right">
        Next send date{" "}
        {toWSDateString(getNextInvoiceSendDate(invoiceTemplate), "monthDate")}
      </WSText.Heading5>
    ) : (
      <WSText.Heading5 color="grey600" align="right">
        Send date{" "}
        {toWSDateString(invoiceTemplate.scheduleDates?.[0]?.date, "monthDate")}
      </WSText.Heading5>
    )
  ) : null;

export const buildInvoiceIcon = (invoice: IInvoice): WSCircleIconProps =>
  invoice.status === InvoiceStatus.Draft
    ? {
        icon: "pencil",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : (invoice.status === InvoiceStatus.Open ||
        invoice.status === InvoiceStatus.Overdue ||
        invoice.status === InvoiceStatus.Pending) &&
      invoice.member.workflowStatus === MemberWorkFlowStatus.Disputed
    ? {
        icon: "warning",
        color: "yellow100",
        secondaryColor: "yellow500"
      }
    : invoice.status === InvoiceStatus.Open
    ? {
        icon: "envelopeCheckmark",
        color: "blue100",
        secondaryColor: "blue500",
        badge:
          invoice.client.workflowStatus === ClientWorkFlowStatus.Declined
            ? {
                text: "!",
                color: "yellow500",
                secondaryColor: "white"
              }
            : undefined
      }
    : invoice.status === InvoiceStatus.Overdue
    ? {
        icon: "envelopeCheckmark",
        color: "blue100",
        secondaryColor: "blue500",
        badge:
          invoice.client.workflowStatus === ClientWorkFlowStatus.Declined
            ? {
                text: "!",
                color: "yellow500",
                secondaryColor: "white"
              }
            : {
                text: "!",
                color: "red500",
                secondaryColor: "white"
              }
      }
    : invoice.status === InvoiceStatus.PaymentInTransit
    ? {
        icon: "successMark",
        color: "grey300",
        secondaryColor: "green500"
      }
    : invoice.status === InvoiceStatus.Paid
    ? invoice.events.depositedAt
      ? {
          icon: "doubleSuccessMark",
          color: "green100",
          secondaryColor: "green500"
        }
      : invoice.events.instantPayoutAt
      ? {
          icon: "doubleSuccessMark",
          color: "green100",
          secondaryColor: "green500"
        }
      : invoice.amountDetails
      ? {
          icon: "successMark",
          color: "grey300",
          secondaryColor: "green500"
        }
      : {
          icon: "doubleSuccessMark",
          color: "green100",
          secondaryColor: "green500"
        }
    : {
        icon: "pencil",
        color: "grey200",
        secondaryColor: "grey600"
      };

const buildInvoiceTemplateIcon = (
  invoiceTemplate: IInvoiceTemplate
): WSCircleIconProps =>
  invoiceTemplate.status === FrequencyAndScheduleStatus.Draft
    ? {
        icon: "pencil",
        color: "grey200",
        secondaryColor: "grey600"
      }
    : {
        icon: "calendar",
        color: "grey200",
        secondaryColor: "grey600"
      };

const EmailClientButton: React.FC<{ invoiceId: string }> = ({ invoiceId }) => {
  const [sendReminder, sendReminderMeta] = useSendInvoiceReminder();
  return (
    <WSButton.Link
      onClick={() => {
        sendReminder({ invoiceId });
      }}
      loading={sendReminderMeta.isLoading}
    >
      Email client
    </WSButton.Link>
  );
};

const InstantPayoutRequestButton: React.FC<{ invoiceId: string }> = ({
  invoiceId
}) => {
  const history = useHistory();

  return (
    <WSButton.Link
      onClick={() => {
        history.push(`/member/invoices/paid/${invoiceId}/instant-deposit`);
      }}
    >
      Receive payout in minutes
    </WSButton.Link>
  );
};
